/**
 * Sentry instrumentation for the React frontend.
 *
 * Reads `VITE_SENTRY_DSN` from the build-time env. Vite bakes `VITE_*`
 * variables into the bundle at **build time** — if you change the value
 * on DigitalOcean App Platform, you must trigger a Force Rebuild on the
 * static-site component or the change won't ship to users.
 *
 * No-ops gracefully when the DSN is missing — useful for local dev.
 */

import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('VITE_SENTRY_DSN not set — frontend error monitoring is disabled');
    return;
  }

  Sentry.init({
    dsn,

    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE ?? 'production',

    // Release tag — couple this with `version` in package.json or override
    // at build time (e.g. inject the git SHA via a build script).
    release: import.meta.env.VITE_SENTRY_RELEASE,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Session replay is opt-in (counts against quota faster than errors).
      // Add `Sentry.replayIntegration()` here later if useful.
    ],

    // 10% trace sampling — see backend instrument.ts for the rationale.
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),

    // Same posture as the backend — never ship PII upstream by default.
    sendDefaultPii: false,

    beforeSend(event) {
      // Strip the in-memory access token in case it ends up in a
      // breadcrumb URL or fetch headers.
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['authorization'];
      }
      return event;
    },
  });

  console.log(
    `Sentry frontend monitoring initialised (environment=${import.meta.env.MODE})`
  );
}
